import { computedFrom, autoinject } from 'aurelia-framework';
import $ from 'jquery';
import { Router } from 'aurelia-router';
import Cookies from 'js-cookie';
import { Customer } from '../../models/customer';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { I18N } from 'aurelia-i18n';
import { BasePage } from '../base-page';
import { EventAggregator } from 'aurelia-event-aggregator';
import { HpfCarouselService } from '../../services/hpf-carousel.service';
import { CarouselItem } from '../../models/carousel-item';
import { RestServiceClient } from '../../smart/services/rest-service-client';
import { PostLoginRouteManager } from '../../helpers/post-login-route-manager';
import { DialogService } from 'aurelia-dialog';
import { LegalTermsDialog } from '../../components/legal-dialog/legal-terms.dialog';

@autoinject
export class Login extends BasePage {
	validationErrors: any = [];
	model: any;
	rememberMe: any;
	loggingIn: any;
	allowEzpay: any;
	linkText: string;
	termsLink: string;
	carouselItems: Array<CarouselItem> = [];
	ezpayTermsAgreed: boolean;
	registerTermsAgreed: boolean;
	displayEzpayAgreeTermsError: boolean;
	displayRegisterAgreeTermsError: boolean;
	spinnerId: string = 'loading-spinner-login';
	showLoginPasswordKey = false;
	showMfa: boolean = false;
	showRememberMeMfa: boolean = false;

	constructor(
		private router: Router,
		private payrazr: PayrazrService,
		private hpfCarouselApi: HpfCarouselService,
		private globalState: State,
		private i18n: I18N,
		private postLoginRouteManager: PostLoginRouteManager,
		eventAggregator: EventAggregator,
		private dialogService: DialogService
	) {
		super(eventAggregator);

		this.model = new Model();
		this.rememberMe = false;
		this.fetchRememberMe();

		this.loggingIn = false;
		this.allowEzpay = false;
	}

	loadTranslations() {
		this.linkText = `<a href="#" >${this.i18n.i18next.t('termsAndConditions', 'terms and conditions')}</a>`;
		this.termsLink = this.i18n.i18next.t('portalLoginAgreeToTermsAndConditions', {
			defaultValue: `I agree to the ${this.linkText}.`, link: this.linkText
		});
	}

	@computedFrom('globalState')
	get PayByTextEnabled() {
		return this.globalState.hasPayByTextEnabledPortfolio;
	}

	togglePassword() {
		this.showLoginPasswordKey = !this.showLoginPasswordKey;
	}

	submit() {
		this.validationErrors = [];
		let validator = new Validator(this.i18n);
		this.globalState.token = ''; // Reset Token on login to avoid old expired tokens causing issues

		if (validator.hasErrors(this.model)) {
			this.validationErrors = validator.errors;

			// WCAG compliance - set focus to announce errors
			let validationSummary = document.getElementsByTagName('validation-summary')[0] as HTMLElement;
			if (validationSummary) {
				validationSummary.focus();
			}

		} else {
			this.loggingIn = true;

			this.payrazr.queryParameters = {};

			const mfaCookie = this.postLoginRouteManager.getMfaCookie(this.model.username);

			this.payrazr.login(this.model.username, this.model.password, mfaCookie)
				.then(response => {
					this.loggingIn = false;
					if (response.isSuccessful) {
						this.handleRemember();
						this.mapCustomer(response.entity.customer);

						if (response.entity.sessionId) {
							this.globalState.business.sessionId = response.entity.sessionId;
						}
						if (response.entity.token) {
							this.setTokenAfterLogin(response.entity.token);
						}
						this.showMfa = response.entity.isMfaRequired;
						this.showRememberMeMfa = response.entity.showRememberMeMfa;
						this.redirectAfterLogin();
					}
					else {
						if (response.message) {
							if (response.message.indexOf('{customerservicephonenumber}') > 0) {
								response.message = response.message.replace('{customerservicephonenumber}', this.globalState.business.portalSettings.csPhone);
							}
							if (response.message === 'invalidUsernameOrPassword') {
								response.message = this.i18n.i18next.t('invalidUsernameOrPassword', 'Invalid username/password');
							}
							this.validationErrors.push(response.message);
						}
						else {
							this.validationErrors.push(this.i18n.i18next.t('invalidUsernameOrPassword', 'Invalid username/password'));
						}
					}
				})
				.catch(error => {
					this.loggingIn = false;
					if (error.status === 401) {
						this.validationErrors.push(this.i18n.i18next.t('invalidUsernameOrPassword', 'Invalid username/password'));
					}
					else if (error.status === 404) {
						this.validationErrors.push(this.i18n.i18next.t('yourAccountNoLongerActive', 'Your account is no longer active. If you feel you have received this message in error, please contact customer service.'));
										}
					else if (error.status === 500) {
						let baseMessage = this.i18n.i18next.t('unableToRetrieveAccountInformation', 'We are unable to retrieve your account information, please try again shortly.');
						if (!this.globalState.business.portalSettings.csPhone) {
							this.validationErrors.push(baseMessage);
						}
						else {
							this.validationErrors.push(baseMessage + ' ' + this.i18n.i18next.t('ifIssuePersistsPleaseContact', 'If the issue persists please contact us at') + ' ' + this.globalState.business.portalSettings.csPhone);
						}
					}
					else {
						this.validationErrors.push(this.i18n.i18next.t('errorConnectingToServer', 'Error connecting to the server.'));
										}

				});
		}
	}

	private mapCustomer(customer: any): void {
		this.globalState.customer = new Customer(this.globalState.business, customer);

		this.globalState.associatedCustomers = [];
		for (let i of customer.associatedCustomers || []) {
			this.globalState.associatedCustomers.push(new Customer(this.globalState.business, i));
		}
	}

	private setTokenAfterLogin(token: string): void {
		this.globalState.isLoggingIn = true;
		this.globalState.token = token;
	}

	mfaClear(event: any): void {
		this.setTokenAfterLogin(event.detail.token);
		this.mapCustomer(event.detail.customer);
		this.redirectAfterLogin(event.detail);
	}

	private redirectAfterLogin(event: any = {}): void {
		if (this.globalState.isLoggingIn
			&& this.globalState.token) {
			const { mustChangePassword } = event;
			let routeResponse = this.postLoginRouteManager.calculateRoute({ changePassword: mustChangePassword });
			if (routeResponse.routeSettings) {
				this.router.currentInstruction.config.settings = routeResponse.routeSettings;
			}
			this.router.navigateToRoute(routeResponse.route);
		}
	}

	private shouldRedirectToPreferredLanguagePage(): boolean {
		if (this.globalState.business?.portalSettings?.businessSettings == null ||
			this.globalState.customer == null) {
			return false;
		}

		return this.globalState.business.portalSettings.businessSettings.enablePreferredLanguageAcknowledgement &&
			this.globalState.customer.preferredLanguageSettings.preferredLanguageCode == null &&
			this.globalState.customer.preferredLanguageSettings.declinedToStatePreferredLanguage == null &&
			this.globalState.customer.preferredLanguageSettings.otherPreferredLanguage == null;
	}

	startRegister = () => {
		this.hideTermsErrors();
		if (!this.globalState.business.portalSettings.businessSettings.hideTerms && !this.registerTermsAgreed) {
			this.displayRegisterAgreeTermsError = true;
			return;
		}

		let hideSignupTerms = document.getElementById('signUp-t-c-agreed') as HTMLInputElement;
		this.globalState.hideSignupTerms = hideSignupTerms.checked ? true : false;
		this.router.navigateToRoute('register');
	}

	startEzpay() {
		this.hideTermsErrors();
		if (!this.globalState.business.portalSettings.businessSettings.hideTerms && !this.ezpayTermsAgreed) {
			this.displayEzpayAgreeTermsError = true;
			return;
		}

		let hideEzpayTerms = document.getElementById('ezpay-t-c-agreed') as HTMLInputElement;
		this.globalState.hideEzpayTerms = hideEzpayTerms.checked ? true : false;

		this.payrazr.startEzpay().then(response => {
			if (response.isSuccessful) {
				this.globalState.business.sessionId = response.entity;
				this.router.navigateToRoute('ezpay');
			}
			else {
				alert(this.i18n.i18next.t('errorEncountered', 'Error encountered.'));
			}
		});
	}

	hideTermsErrors() {
		this.displayEzpayAgreeTermsError = false;
		this.displayRegisterAgreeTermsError = false;
	}

	handleRemember() {
		if (this.rememberMe) {
			Cookies.set('RememberMe', this.model.username, { expires: 60, path: '' });
		} else {
			Cookies.remove('RememberMe', { path: '' });
		}
	}

	fetchRememberMe() {
		let value = Cookies.get('RememberMe');
		if (value) {
			this.model.username = value;
			this.rememberMe = true;
		}
	}

	getCarousel() {
		if (this.globalState.business) {
			this.hpfCarouselApi.getCarousel(this.globalState.business.portalId).then(
				(result) => {
					let interval = (result.interval || 6) * 1000;
					result.items.forEach(item => {
						this.carouselItems.push(new CarouselItem(item));
					});
					$('#carousel').carousel({ interval });
				}
			);
		}
	}

	attached() {
		super.attached();
		if (this.globalState.business.portalSettings) {
			this.allowEzpay = this.globalState.business.portalSettings.ezpay.enabled;
		}

		this.getCarousel();
		this.updateSession();
	}

	updateSession() {
		this.payrazr.resetSession().then(response => {
			if (response.isSuccessful && !!response.entity) {
				this.globalState.business.sessionId = response.entity;
			}
		});
	}

	get isSubmitting() {

		// WCAG compliance - announcing loading
		if (this.loggingIn) {
			let loadingIcon = document.getElementById(this.spinnerId);
			if (loadingIcon) {
				loadingIcon.focus();
			}
		}

		return this.loggingIn;
	}

	get showCarousel() {
		return this.carouselItems && this.carouselItems.length;
	}

	showLegalModal(content:string) {
		if (content === 'terms') {
			this.dialogService.open({ viewModel: LegalTermsDialog, lock: false });
		}
	}
}

class Model {
	username = '';
	password = '';
}

class Validator {
	errors: any = [];

	constructor(private i18n: I18N) {
	}

	hasErrors(model) {
		if (model.username.length === 0) {
			this.errors.push(this.i18n.i18next.t('pleaseEnterUsername', 'Please enter your username'));
		}

		if (model.password.length === 0) {
			this.errors.push(this.i18n.i18next.t('pleaseEnterPassword', 'Please enter your password'));
		}

		return this.errors.length > 0;
	}
}
